/**
 * POS-FULFILLMENT MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 *
 * posService :: POS orders + product-sample requests over RLS (Supabase Auth).
 *
 * Ported from the source app, where every call went through a service-role
 * `pos-samples` edge function (Azure-AD / MSAL model). Brand Command uses
 * Supabase Auth + RLS, so — like depletionService / referenceDataService — we
 * talk to the module's tables DIRECTLY with the authenticated client. RLS pins
 * every row to the caller's account via is_account_member(account_id) (see the
 * module's db/schema.sql).
 *
 * Returned row shapes match what the ported components expect:
 *  - POS orders + sample requests are mapped to the source app's camelCase envelope.
 *  - pos_items / pos_delivery_contacts / product_samples are returned as raw
 *    snake_case rows (the components read them that way).
 *
 * Writes inject the caller's account_id (so the RLS WITH CHECK passes) and only
 * send whitelisted columns; ids / order_number / status defaults / timestamps
 * are DB-controlled, never spread from the body. Errors log server-side detail
 * and return a generic message to the caller.
 *
 * Email notifications (the the source app send-pos-shipped-email / send-sample-shipped-email
 * Microsoft-Graph satellites + per-user notify_* preferences) are DEFERRED in
 * Brand Command — same status as the depletions low-inventory email. The two
 * invoke* helpers below are intentional no-ops so the components are unchanged;
 * wire them to the Brevo pipeline when notifications land.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import {
  APPROVAL,
  APPROVAL_DECISIONS,
  isShipBlocked,
  shipBlockedMessage,
  normalizeRejectReason,
} from "../utils/approval.js";

// ── Active account (the tenant whose POS/sample data we manage) ──────────────
//
// Resolved once and cached for the life of the page. Switching workspace does
// NOT go through a reset hook — `WorkspaceSwitcher` does a full
// `window.location.reload()`, which drops this module and its cache with it.
// There used to be an exported `resetPosAccount()` here that nothing ever
// called; it was removed because a cleanup hook that looks required but isn't
// wired is worse than none — it reads as proof the switch is handled in JS.
// ⚠️ If the reload is ever traded for an in-place switch, this cache (and the
// identical ones in depletions-inventory and distributor-orders) must be
// cleared at that moment, or the user keeps reading the previous tenant.
let _accountIdPromise = null;

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// Friendly, generic errors for RLS / FK / unique violations.
function writeError(where, error) {
  console.error(`[posService.${where}]`, error);
  if (error?.code === "42501" || /row-level security/i.test(error?.message ?? "")) {
    return new Error("You don't have permission to make that change.");
  }
  if (error?.code === "23503") return new Error("That item is still in use and can't be deleted yet.");
  if (error?.code === "23505") return new Error("That entry already exists.");
  // Migration 0097's approval guard raises check_violation when a pending or
  // rejected sample request is moved to 'Shipped'. The UI removes that control
  // long before here, so reaching this is a race (two tabs, a stale grid) — but
  // "Could not save your changes." on THAT is the exact dead end this gate was
  // built to avoid, so it gets its own sentence. We answer with our own copy
  // rather than echoing the DB text, which names the row id and the column.
  if (error?.code === "23514" && /has not been approved/i.test(error?.message ?? "")) {
    return new Error(
      "That request came in through the public sample form and hasn't been approved yet, " +
      "so it can't be marked Shipped. Reload the screen and use Review approval on the row."
    );
  }
  return new Error("Could not save your changes.");
}

// Map only known keys from `src` (any-case) through `map` (srcKey -> column);
// coerce empties via `coerce`. Unknown keys (id, section, …) are dropped.
function pick(src, map, coerce = {}) {
  const out = {};
  for (const [k, col] of Object.entries(map)) {
    if (src[k] === undefined) continue;
    const fn = coerce[col];
    out[col] = fn ? fn(src[k]) : src[k];
  }
  return out;
}

const orNull = (v) => (v === "" || v === undefined ? null : v);
const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
const intOrNull = (v) => (v === "" || v == null ? null : parseInt(v, 10) || null);

// =====================================================================
// Sample Requests
// =====================================================================

const SAMPLE_COL_MAP = {
  submitter: "submitter_name",
  submitterName: "submitter_name",
  submitterEmail: "submitter_email",
  shipToCompany: "ship_to_company",
  address: "address",
  state: "state",
  contactName: "contact_name",
  contactPhone: "contact_phone",
  contactEmail: "contact_email",
  orderDate: "order_date",
  requestedShipDate: "requested_ship_date",
  product: "product",
  quantity: "quantity",
  notes: "notes",
  status: "status",
  shipDate: "ship_date",
  estDelivery: "est_delivery",
  carrier: "carrier",
  tracking: "tracking",
};
// ⚠️ `approval_status`, `source`, `embed_key_id`, `approved_by`, `approved_at`
// and `rejected_reason` are DELIBERATELY ABSENT from this map and must stay
// absent. `updateSampleRequest` is called with a whole spread request object
// (ShippingEditModal does `{ ...request, status, … }`), so anything listed here
// is writable by any caller that happens to be holding a row. Approval moves
// through `setSampleApproval` below, which accepts two literal values and
// nothing else; `approved_by`/`approved_at` are stamped by 0097's trigger from
// `auth.uid()`, never sent from here.
const SAMPLE_COERCE = {
  order_date: orNull, requested_ship_date: orNull, ship_date: orNull,
  est_delivery: orNull, carrier: orNull, tracking: orNull, quantity: intOrNull,
};

function deriveSection(status) {
  if (status === "Shipped") return "shipped";
  return "new";
}

function normalizeSampleRequest(r) {
  return {
    id: r.id,
    submitter: r.submitter_name ?? "",
    shipToCompany: r.ship_to_company ?? "",
    address: r.address ?? "",
    state: r.state ?? "",
    contactName: r.contact_name ?? "",
    contactPhone: r.contact_phone ?? "",
    contactEmail: r.contact_email ?? "",
    orderDate: r.order_date ?? "",
    requestedShipDate: r.requested_ship_date ?? "",
    product: r.product ?? "",
    quantity: r.quantity ?? "",
    notes: r.notes ?? "",
    submitterEmail: r.submitter_email ?? "",
    status: r.status ?? "Not Shipped",
    shipDate: r.ship_date ?? "",
    estDelivery: r.est_delivery ?? "",
    carrier: r.carrier ?? "",
    tracking: r.tracking ?? "",
    section: deriveSection(r.status),
    // ── Provenance + the review gate (migration 0097) ──────────────────────
    // Read-only on the client. `source` is what separates a rep's request from
    // one an anonymous caller typed a ship-to address into; `approvalStatus` is
    // what the DB trigger consults before it lets a row reach 'Shipped'.
    // Historic rows have neither column set, and default to exactly today's
    // behaviour ('app' / 'not_required').
    source: r.source ?? "app",
    approvalStatus: r.approval_status ?? APPROVAL.NOT_REQUIRED,
    approvedAt: r.approved_at ?? "",
    rejectedReason: r.rejected_reason ?? "",
    embedKeyId: r.embed_key_id ?? null,
  };
}

/** All product sample requests, newest first. */
export async function loadSampleRequests() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_sample_requests")
    .select("*")
    .eq("account_id", acct)
    .order("created_at", { ascending: false });
  if (error) { console.error("[posService.loadSampleRequests]", error); throw new Error("Could not load sample requests."); }
  return (data ?? []).map(normalizeSampleRequest);
}

/** Create a sample request (camelCase fields). Returns the raw inserted row. */
export async function createSampleRequest(fields) {
  const acct = await activeAccountId();
  const row = { account_id: acct, ...pick(fields, SAMPLE_COL_MAP, SAMPLE_COERCE) };
  const { data, error } = await supabase
    .from("product_sample_requests")
    .insert(row)
    .select("*")
    .single();
  if (error) throw writeError("createSampleRequest", error);
  // Item 198: confirm the placement. Fire-and-forget on purpose — the request
  // is committed, and a mail failure must not read as a failed request. The
  // edge function re-derives the recipient from the row, so nothing about who
  // gets this comes from here.
  invokeSampleReceivedEmail(data).catch(err =>
    console.error("[posService.createSampleRequest] confirmation:", err)
  );
  return data;
}

/** Update a sample request. `fields` may be a single {field:value} or a whole
 *  spread request object (camelCase) — only whitelisted columns are written. */
export async function updateSampleRequest(id, fields) {
  const acct = await activeAccountId();
  const patch = pick(fields, SAMPLE_COL_MAP, SAMPLE_COERCE);
  if (Object.keys(patch).length === 0) return { id };

  // The gate, ahead of the trigger. 0097 RAISES on a pending/rejected row moving
  // to 'Shipped', and a raised exception surfaces as a generic "Could not save
  // your changes." with no way forward. Re-read the row's CURRENT approval
  // status — not whatever the caller's copy says, which may be minutes stale —
  // and refuse with the sentence that names the fix.
  if (patch.status === "Shipped") {
    // `.maybeSingle()` is safe here: filtered on the primary key, not on RLS
    // visibility alone (SECURITY_RULES — "RLS grants VISIBILITY, not CARDINALITY").
    const { data: current, error: readErr } = await supabase
      .from("product_sample_requests")
      .select("approval_status")
      .eq("account_id", acct)
      .eq("id", id)
      .maybeSingle();
    if (readErr) {
      console.error("[posService.updateSampleRequest/approvalCheck]", readErr);
      throw new Error("Could not check whether that request is approved. Try again.");
    }
    if (current && isShipBlocked(current)) throw new Error(shipBlockedMessage(current));
  }

  const { error } = await supabase
    .from("product_sample_requests")
    .update(patch)
    .eq("account_id", acct)
    .eq("id", id);
  if (error) throw writeError("updateSampleRequest", error);
  return { id };
}

/**
 * Rule on a sample request that came through the public form (migration 0097).
 *
 * `decision` is checked against a two-element literal list before anything is
 * built — the caller cannot reach `approval_status` with an arbitrary string,
 * and there is no code path here that writes 'not_required' (which would let a
 * reviewer launder an embed row into a rep's row and lose the provenance the
 * 0097 CHECK constraint exists to preserve).
 *
 * The update is filtered on `approval_status = 'pending'`, so a second reviewer
 * acting on a stale grid changes nothing and is told so, rather than silently
 * overwriting the first ruling. `approved_by` / `approved_at` are stamped by the
 * trigger from `auth.uid()`; we never send them.
 */
export async function setSampleApproval(id, decision, reason) {
  if (!APPROVAL_DECISIONS.includes(decision)) {
    throw new Error("Unknown approval decision.");
  }
  const acct = await activeAccountId();

  let patch;
  if (decision === APPROVAL.APPROVED) {
    patch = { approval_status: APPROVAL.APPROVED, rejected_reason: null };
  } else {
    const why = normalizeRejectReason(reason);
    if (!why) throw new Error("Give a reason for rejecting this request.");
    patch = { approval_status: APPROVAL.REJECTED, rejected_reason: why };
  }

  const { data, error } = await supabase
    .from("product_sample_requests")
    .update(patch)
    .eq("account_id", acct)
    .eq("id", id)
    .eq("approval_status", APPROVAL.PENDING)
    .select("*")
    .maybeSingle();
  if (error) throw writeError("setSampleApproval", error);
  if (!data) {
    throw new Error("That request is no longer waiting for approval — someone else may have ruled on it. Reload the screen.");
  }
  return normalizeSampleRequest(data);
}

/** Approve a pending embed request so it can move through fulfilment normally. */
export function approveSampleRequest(id) {
  return setSampleApproval(id, APPROVAL.APPROVED);
}

/** Reject a pending embed request. The reason is required and is stored on the row. */
export function rejectSampleRequest(id, reason) {
  return setSampleApproval(id, APPROVAL.REJECTED, reason);
}

export async function deleteSampleRequest(id) {
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("product_sample_requests")
    .delete()
    .eq("account_id", acct)
    .eq("id", id);
  if (error) throw writeError("deleteSampleRequest", error);
  return { id };
}

// =====================================================================
// POS Orders (+ long-format order lines)
// =====================================================================

const POS_ORDER_COL_MAP = {
  submitterName: "submitter_name",
  submitterEmail: "submitter_email",
  companyContact: "company_contact",
  shipToCompany: "ship_to_company",
  shipToAddress: "ship_to_address",
  shipToContactName: "ship_to_contact_name",
  shipToContactPhone: "ship_to_contact_phone",
  shipToContactEmail: "ship_to_contact_email",
  orderDate: "order_date",
  requestedShipDate: "requested_ship_date",
  specialInstructions: "special_instructions",
  notifyDistributor: "notify_distributor",
  // shipping fields (POSShippingModal / inline edit)
  status: "status",
  shipDate: "ship_date",
  estDelivery: "est_delivery",
  carrier: "carrier",
  tracking: "tracking",
  shippingCost: "shipping_cost",
};
const POS_ORDER_COERCE = {
  order_date: orNull, requested_ship_date: orNull, ship_date: orNull,
  est_delivery: orNull, carrier: orNull, tracking: orNull, shipping_cost: numOrNull,
};

function normalizePOSOrder(r) {
  return {
    id: r.id,
    orderNumber: r.order_number ?? "",
    status: r.status ?? "Pending",
    companyContact: r.company_contact ?? "",
    submitterName: r.submitter_name ?? "",
    submitterEmail: r.submitter_email ?? "",
    shipToCompany: r.ship_to_company ?? "",
    shipToAddress: r.ship_to_address ?? "",
    shipToContactName: r.ship_to_contact_name ?? "",
    shipToContactPhone: r.ship_to_contact_phone ?? "",
    shipToContactEmail: r.ship_to_contact_email ?? "",
    orderDate: r.order_date ?? "",
    requestedShipDate: r.requested_ship_date ?? "",
    specialInstructions: r.special_instructions ?? "",
    shipDate: r.ship_date ?? "",
    estDelivery: r.est_delivery ?? "",
    carrier: r.carrier ?? "",
    tracking: r.tracking ?? "",
    shippingCost: r.shipping_cost ?? null,
    notifyDistributor: r.notify_distributor !== false,
    createdAt: r.created_at ?? "",
    items: r._items ?? {},
  };
}

/** All POS orders (newest first) with `_items` ({item_key: qty}) merged in. */
export async function loadPOSOrders() {
  const acct = await activeAccountId();
  const { data: orders, error } = await supabase
    .from("pos_orders")
    .select("*")
    .eq("account_id", acct)
    .order("created_at", { ascending: false });
  if (error) { console.error("[posService.loadPOSOrders]", error); throw new Error("Could not load POS orders."); }
  const rows = orders ?? [];
  if (rows.length === 0) return [];

  const ids = rows.map((o) => o.id);
  const { data: lines, error: lErr } = await supabase
    .from("pos_order_lines")
    .select("order_id, item_key, quantity")
    .eq("account_id", acct)
    .in("order_id", ids);
  if (lErr) { console.error("[posService.loadPOSOrders/lines]", lErr); throw new Error("Could not load POS orders."); }

  const itemsByOrder = {};
  for (const l of lines ?? []) {
    (itemsByOrder[l.order_id] ??= {})[l.item_key] = l.quantity;
  }
  return rows.map((o) => normalizePOSOrder({ ...o, _items: itemsByOrder[o.id] ?? {} }));
}

/**
 * Create a POS order + its order lines. `orderFields` is camelCase (NewPOSModal),
 * `itemFields` is a {item_key: qtyString} dict. Validates every submitted
 * item_key against the account's pos_items catalog; rolls back the order row if
 * line insertion fails (the source app's POS-1010 behavior). Returns the raw inserted row.
 */
export async function createPOSOrder(orderFields, itemFields) {
  const acct = await activeAccountId();

  // Lines to insert (qty > 0 only).
  const wanted = Object.entries(itemFields || {})
    .map(([item_key, q]) => ({ item_key, quantity: parseInt(q, 10) || 0 }))
    .filter((l) => l.quantity > 0);

  // Validate item_keys against the catalog before touching the orders table.
  // `active` and `min_order_quantity` are checked here for the same reason
  // pos-order-public checks them: they are tenant settings, and an order placed
  // in the app must not be able to do what the same order through the public
  // embed is refused. The modal checks both too — this is the backstop.
  if (wanted.length > 0) {
    const { data: catalog, error: cErr } = await supabase
      .from("pos_items")
      .select("item_key, name, min_order_quantity")
      .eq("account_id", acct)
      .eq("active", true)
      .in("item_key", wanted.map((l) => l.item_key));
    if (cErr) throw writeError("createPOSOrder/catalog", cErr);
    const byKey = new Map((catalog ?? []).map((r) => [r.item_key, r]));
    const unknown = wanted.filter((l) => !byKey.has(l.item_key)).map((l) => l.item_key);
    if (unknown.length > 0) throw new Error(`Unknown or inactive POS item(s): ${unknown.join(", ")}`);
    for (const l of wanted) {
      const item = byKey.get(l.item_key);
      const min = item.min_order_quantity ?? 1;
      if (l.quantity < min) throw new Error(`${item.name} has a minimum order of ${min}.`);
    }
  }

  const orderRow = { account_id: acct, ...pick(orderFields, POS_ORDER_COL_MAP, POS_ORDER_COERCE) };
  const { data: order, error: oErr } = await supabase
    .from("pos_orders")
    .insert(orderRow)
    .select("*")
    .single();
  if (oErr) throw writeError("createPOSOrder", oErr);

  if (wanted.length > 0) {
    const lineRows = wanted.map((l) => ({ account_id: acct, order_id: order.id, item_key: l.item_key, quantity: l.quantity }));
    const { error: lErr } = await supabase.from("pos_order_lines").insert(lineRows);
    if (lErr) {
      // Roll back the order so we never leave an order with no lines.
      await supabase.from("pos_orders").delete().eq("account_id", acct).eq("id", order.id);
      throw writeError("createPOSOrder/lines", lErr);
    }
  }
  return order;
}

export async function updatePOSOrderShipping(id, fields) {
  const acct = await activeAccountId();
  const patch = pick(fields, POS_ORDER_COL_MAP, POS_ORDER_COERCE);
  const { error } = await supabase.from("pos_orders").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updatePOSOrderShipping", error);
  return { id };
}

/** Inline single-field edit. `field` is a camelCase order key. */
export async function updatePOSOrderField(id, field, value) {
  const col = POS_ORDER_COL_MAP[field];
  if (!col) throw new Error(`Field not editable: ${field}`);
  const acct = await activeAccountId();
  const coerce = POS_ORDER_COERCE[col];
  const patch = { [col]: coerce ? coerce(value) : value };
  const { error } = await supabase.from("pos_orders").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updatePOSOrderField", error);
  return { id };
}

export async function deletePOSOrder(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("pos_orders").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deletePOSOrder", error);
  return { id };
}

// =====================================================================
// POS & Sample catalogs (POS items, delivery contacts, product samples) now
// live in shared/referenceDataService — set up in System Settings, consumed
// here. Re-exported so this module's components keep importing them from
// posService unchanged. decrementPOSInventory (order-time, transactional) stays.
// =====================================================================
export {
  loadPOSItems, createPOSItem, updatePOSItem, updatePOSItemImageUrl, deletePOSItem, uploadPOSItemImage,
  loadPOSDeliveryContacts, createPOSDeliveryContact, updatePOSDeliveryContact, deletePOSDeliveryContact,
  loadProductSamples, loadAllProductSamplesAdmin, createProductSample, updateProductSample, deleteProductSample,
  // pos-item-images went PRIVATE in migration 0069 (backlog item 55), so
  // pos_items.image_url is a storage path, not a servable URL. Components must
  // resolve it through here before rendering.
  signPOSItemImages, signPOSItemImage,
  // The tenant's own sales territory. Used to surface the states a brand
  // actually sells in ahead of the full 50 in the sample-request picker —
  // ahead of, never instead of: a sample legitimately ships to a state the
  // brand hasn't opened yet (backlog item 61).
  listSalesStates,
} from "../../../shared/utils/referenceDataService.js";

/** Best-effort inventory decrement on order submit (fire-and-forget upstream). */
export async function decrementPOSInventory(posItems, qtys) {
  const acct = await activeAccountId();
  const updates = (posItems || [])
    .map((it) => ({ id: it.id, next: Math.max(0, (Number(it.current_inventory) || 0) - (parseInt(qtys?.[it.item_key], 10) || 0)), qty: parseInt(qtys?.[it.item_key], 10) || 0 }))
    .filter((u) => u.qty > 0);
  for (const u of updates) {
    const { error } = await supabase.from("pos_items").update({ current_inventory: u.next }).eq("account_id", acct).eq("id", u.id);
    if (error) console.error("[posService.decrementPOSInventory]", error); // non-fatal
  }
  return { updated: updates.length };
}

// =====================================================================
// Shipped-notification emails — via the pos-shipped-notify edge function
// =====================================================================
// the source app fired Microsoft-Graph emails (send-pos-shipped-email /
// send-sample-shipped-email) gated by per-user notify_* preferences. Brand
// Command routes them through `pos-shipped-notify`, which validates the caller,
// re-reads the row, derives the recipients from it, and hands off to the shared
// Brevo `send-email` pipeline (branding + email_log + the master enable switch).
//
// We deliberately send ONLY the row id. Recipients, tracking and the shipped
// status are all re-derived server-side — the client cannot aim this at an
// arbitrary address, and send-email's internal secret never reaches the bundle.
// The `shipping` / `userEmail` arguments are kept for call-site compatibility
// with the ported components and are intentionally unused.
//
// Callers invoke these fire-and-forget (`.catch`) after their own write has
// resolved, so a mail failure can never block a shipping update.

async function notifyShipped(orderType, orderId, event = "shipped") {
  if (!orderId) return { sent: false, reason: "no_order" };
  const { data, error } = await supabase.functions.invoke("pos-shipped-notify", {
    body: { orderType, orderId, event },
  });
  if (error) {
    console.error(`[posService.notifyShipped:${orderType}:${event}]`, error);
    return { sent: false, reason: "invoke_failed" };
  }
  return data ?? { sent: false, reason: "no_response" };
}

export async function invokeSampleShippedEmail(request /* , shipping, userEmail */) {
  return notifyShipped("sample", request?.id);
}

export async function invokePOSShippedEmail(order /* , shipping, userEmail */) {
  return notifyShipped("pos", order?.id);
}

/**
 * Item 198. A sample request used to send nothing at all until the box shipped
 * — the placement confirmation POS gets on its public embed path had no sample
 * equivalent. Fired by createSampleRequest, best-effort: a mail problem must
 * never fail a request that is already written.
 */
export async function invokeSampleReceivedEmail(request) {
  return notifyShipped("sample", request?.id, "received");
}

// Item 204: every one of these used to be a 200 that the UI dropped on the
// floor, so "the customer was told" and "nothing left the building" looked
// identical to the person marking the row Shipped. Callers turn a `sent:false`
// into a visible warning using this map.
const SHIPPED_NOTIFY_REASONS = {
  no_order:            "the record had no id",
  invoke_failed:       "the notification service could not be reached",
  no_response:         "the notification service returned nothing",
  not_shipped:         "the record is not marked Shipped yet",
  no_recipients:       "there is no email address on the record to send to",
  email_not_configured:"email is not configured on the server",
  send_failed:         "the email provider rejected it — check Admin → Email Log",
  error:               "the notification service hit an error",
};

/** Human-readable warning for a shipped-notify result, or null when it sent. */
export function shippedNotifyWarning(result) {
  if (result?.sent) return null;
  const why = SHIPPED_NOTIFY_REASONS[result?.reason] ?? "an unknown problem";
  return `Marked Shipped, but the notification email was NOT sent — ${why}.`;
}

// =====================================================================
// Shipping labels (UPS) — via the create-shipping-label edge function
// =====================================================================
// Carrier credentials + config are managed in System Settings → Shipping
// Carriers (shipping_carrier_settings is account-scoped per-tenant config we
// only READ here for gating/defaults — same posture as sales_states). Label
// creation, storage, and the order stamp happen server-side in the edge fn.

/** Non-secret carrier config for gating the "Create label" UI + form defaults. */
export async function loadShippingCarrier(carrier = "ups") {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("shipping_carrier_settings")
    .select("enabled, has_credentials, environment, default_service_code, default_label_format, default_weight_lbs, default_length_in, default_width_in, default_height_in")
    .eq("account_id", acct).eq("carrier", carrier).maybeSingle();
  if (error) { console.error("[posService.loadShippingCarrier]", error); return null; }
  return data;
}

// ⚠️ Each call names the workspace. These functions hold the service-role key,
// so RLS is not what scopes them, and they cannot see the workspace switcher.
// Guessing meant a label — and its postage charge — could be billed to a
// different tenant's UPS account than the one the order belongs to. The function
// re-verifies membership of the id we send, so a wrong id fails rather than
// silently acting somewhere else.

/** Create a UPS label for an order. input: { orderType:'pos'|'sample', orderId, shipTo, serviceCode, weightLbs, length, width, height, labelFormat, markShipped }. */
export async function createShippingLabel(input) {
  const accountId = await activeAccountId();
  const { data, error } = await supabase.functions.invoke("create-shipping-label", { body: { ...input, accountId } });
  if (error) throw new Error(error.message || "Could not create the label.");
  if (!data?.ok) throw new Error(data?.error || "Could not create the label.");
  return data; // { shipmentId, trackingNumber, labelUrl, labelFormat, totalCharge, currency }
}

/** Fresh signed URL for an existing label (reprint). */
export async function getLabelUrl(shipmentId) {
  const accountId = await activeAccountId();
  const { data, error } = await supabase.functions.invoke("create-shipping-label", { body: { action: "label_url", shipmentId, accountId } });
  if (error) throw new Error(error.message || "Could not load the label.");
  if (!data?.ok) throw new Error(data?.error || "Could not load the label.");
  return data.labelUrl;
}

/** Void a UPS label and clear the order's tracking/ship date. */
export async function voidShippingLabel(shipmentId) {
  const accountId = await activeAccountId();
  const { data, error } = await supabase.functions.invoke("void-shipping-label", { body: { shipmentId, accountId } });
  if (error) throw new Error(error.message || "Could not void the label.");
  if (!data?.ok) throw new Error(data?.error || "Could not void the label.");
  return data;
}

/** Existing shipments (labels) for an order, newest first. */
export async function loadShipmentsForOrder(orderType, orderId) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("shipments")
    .select("id, tracking_number, label_format, status, total_charge, currency, created_at")
    .eq("account_id", acct).eq("order_type", orderType).eq("order_id", orderId)
    .order("created_at", { ascending: false });
  if (error) { console.error("[posService.loadShipmentsForOrder]", error); return []; }
  return data ?? [];
}
